import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Loader2, ShieldCheck, Sparkles, TriangleAlert } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { auth } from "../lib/firebase";
import { apiJson } from "../lib/api";
import { useAppStore } from "../store/useAppStore";
import { toast } from "sonner";

type BillingPlanId = "foundation" | "operator" | "atlas";
type CheckoutPhase = "preparing" | "creating" | "redirecting" | "error";

const PLAN_LABELS: Record<BillingPlanId, string> = {
  foundation: "Foundation",
  operator: "Operator",
  atlas: "Atlas",
};

const PLAN_DESCRIPTIONS: Record<BillingPlanId, string> = {
  foundation: "Best for solo investors getting started.",
  operator: "For active teams and higher-volume workflows.",
  atlas: "For power users who want the deepest workflow.",
};

const VALID_PLANS = new Set<BillingPlanId>(["foundation", "operator", "atlas"]);

function normalizeReturnTo(value: string | null): "/pricing" | "/settings" {
  return value === "/pricing" ? "/pricing" : "/settings";
}

function buildLoginRedirect(planId: BillingPlanId, returnTo: "/pricing" | "/settings") {
  return `/login?redirect=${encodeURIComponent(`/checkout/${planId}?returnTo=${returnTo}`)}`;
}

export default function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { planId: routePlanId } = useParams();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAppStore();
  const startedRef = useRef(false);
  const [attempt, setAttempt] = useState(0);
  const [phase, setPhase] = useState<CheckoutPhase>("preparing");
  const [status, setStatus] = useState("Preparing secure checkout...");
  const [error, setError] = useState<string | null>(null);

  const planId = useMemo(() => {
    if (!routePlanId || !VALID_PLANS.has(routePlanId as BillingPlanId)) {
      return null;
    }
    return routePlanId as BillingPlanId;
  }, [routePlanId]);

  const returnTo = normalizeReturnTo(searchParams.get("returnTo"));
  const returnLabel = returnTo === "/pricing" ? "pricing" : "settings";
  const loginRedirect = planId ? buildLoginRedirect(planId, returnTo) : "/login?redirect=/pricing";
  const checkoutSource = (location.state as { source?: string } | null)?.source === "settings" ? "settings" : "pricing";
  const sourceLabel = checkoutSource === "settings" ? "Settings" : "Pricing";

  const retryCheckout = useCallback(() => {
    setError(null);
    setPhase("preparing");
    setStatus("Preparing secure checkout...");
    startedRef.current = false;
    setAttempt((current) => current + 1);
  }, []);

  useEffect(() => {
    if (!planId) {
      setPhase("error");
      setError("That checkout link does not include a valid paid plan.");
      setStatus("Choose a paid plan first.");
      toast.error("Choose a paid plan first.");
      return;
    }

    if (!isAuthenticated || !auth.currentUser) {
      navigate(loginRedirect, { replace: true });
      return;
    }

    if (startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;

    const startCheckout = async () => {
      try {
        setError(null);
        setPhase("creating");
        setStatus(`Creating a secure Stripe session for ${PLAN_LABELS[planId]}...`);

        const response = await apiJson<{ url?: string; error?: string }>("/api/stripe/create-checkout-session", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: auth.currentUser?.uid,
            email: auth.currentUser?.email,
            planId,
            returnTo,
          }),
        });

        if (cancelled) return;

        if (!response.url) {
          throw new Error(response.error || "Unable to open Stripe checkout.");
        }

        setPhase("redirecting");
        setStatus("Redirecting to Stripe Checkout...");
        window.location.replace(response.url);
      } catch (err: any) {
        if (cancelled) return;
        const message = err?.message || "Could not start checkout.";
        setError(message);
        setPhase("error");
        setStatus("Checkout could not be started.");
        toast.error(message);
        startedRef.current = false;
      }
    };

    void startCheckout();

    return () => {
      cancelled = true;
    };
  }, [attempt, isAuthenticated, loginRedirect, navigate, planId, returnTo]);

  const goBack = () => {
    navigate(returnTo === "/pricing" ? "/pricing" : "/settings");
  };

  const statusTone = phase === "error" ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-slate-50";
  const statusIcon = phase === "error" ? (
    <TriangleAlert className="mt-0.5 h-5 w-5 text-rose-600" />
  ) : (
    <Loader2 className="mt-0.5 h-5 w-5 animate-spin text-blue-600" />
  );

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-3">
          <Button variant="outline" onClick={goBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to {returnLabel}
          </Button>
          <Badge variant="outline" className="text-slate-600">Secure billing route</Badge>
        </div>

        <Card className="overflow-hidden border-slate-200 shadow-sm">
          <div className="h-1 bg-gradient-to-r from-blue-500 via-cyan-400 to-emerald-400" />
          <CardHeader className="space-y-3">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-blue-600" />
              <CardTitle className="text-2xl">Preparing Stripe Checkout</CardTitle>
            </div>
            {planId ? (
              <CardDescription className="text-base">
                You’re being routed to Stripe for {PLAN_LABELS[planId]} — {PLAN_DESCRIPTIONS[planId]}
              </CardDescription>
            ) : (
              <CardDescription className="text-base">Select a paid plan to continue.</CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-5">
            <div className={`rounded-2xl border p-5 ${statusTone}`}>
              <div className="flex items-start gap-3">
                {statusIcon}
                <div className="space-y-1">
                  <p className="font-medium text-slate-900">{status}</p>
                  <p className="text-sm leading-6 text-slate-600">
                    We create the Checkout Session on the server, then Stripe takes over the payment step.
                  </p>
                </div>
              </div>
            </div>

            {planId ? (
              <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-5 sm:grid-cols-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Plan</p>
                  <p className="mt-1 font-semibold text-slate-900">{PLAN_LABELS[planId]}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Return destination</p>
                  <p className="mt-1 font-semibold text-slate-900">{returnLabel}</p>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Started from</p>
                  <p className="mt-1 font-semibold text-slate-900">{sourceLabel}</p>
                </div>
              </div>
            ) : null}

            {phase !== "error" ? (
              <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600 sm:grid-cols-3">
                <div className="space-y-2 rounded-xl bg-slate-50 p-3">
                  <div className="flex items-center gap-2 font-medium text-slate-800">
                    <CheckCircle2 className={`h-4 w-4 ${phase === "creating" || phase === "redirecting" ? "text-emerald-500" : "text-blue-500"}`} />
                    1. Verify access
                  </div>
                  <p>Confirming your account before checkout opens.</p>
                </div>
                <div className="space-y-2 rounded-xl bg-slate-50 p-3">
                  <div className="flex items-center gap-2 font-medium text-slate-800">
                    <CheckCircle2 className={`h-4 w-4 ${phase === "redirecting" ? "text-emerald-500" : "text-blue-500"}`} />
                    2. Create session
                  </div>
                  <p>Generating the live Stripe Checkout Session on the server.</p>
                </div>
                <div className="space-y-2 rounded-xl bg-slate-50 p-3">
                  <div className="flex items-center gap-2 font-medium text-slate-800">
                    <Sparkles className={`h-4 w-4 ${phase === "redirecting" ? "text-emerald-500" : "text-blue-500"}`} />
                    3. Open Stripe
                  </div>
                  <p>Handing you off to checkout.stripe.com for payment.</p>
                </div>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-3">
              <Button onClick={goBack} variant="outline">
                Return to {returnLabel}
              </Button>
              {planId ? (
                <Button
                  className="bg-blue-600 text-white hover:bg-blue-700"
                  onClick={() => navigate(loginRedirect)}
                  variant="default"
                >
                  Sign in again
                </Button>
              ) : null}
              {phase === "error" && planId ? (
                <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={retryCheckout}>
                  Retry checkout
                </Button>
              ) : null}
            </div>

            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
                {error}
              </div>
            ) : null}
          </CardContent>
        </Card>

        <div className="rounded-2xl border border-blue-100 bg-blue-50/70 p-4 text-sm leading-6 text-slate-700">
          Tip: if you came from Settings or Pricing, the checkout page will keep your return target and send you back after payment or cancellation.
        </div>
      </div>
    </div>
  );
}
